import moment from "moment-timezone";
import { getSchedulingTimezones, isSelectableSchedulingTimezone } from "./ianaTimezoneOptions";
// logWarn rather than a bare console call: Datadog runs with forwardConsoleLogs: ["error"], so a
// plain warn never leaves the developer's own inspector. These lines exist to explain a silent
// fallback in PRODUCTION, which is precisely where such a call cannot be read — the same
// invisible-failure shape this change is meant to remove. logWarn still writes to the console.
import { logWarn } from "./logError";

/**
 * Log a hint-suppression reason at most once per distinct cause.
 *
 * formatCrossTimezoneHint is called from a Vue COMPUTED (ActionSchedule.vue's crossTimezoneHint), so it
 * re-runs on every change to any dependency — a keystroke in the date field, a re-render. A
 * persistently-bad stored zone would therefore re-log forever, which turns a useful diagnostic into the
 * noise that gets pipelines muted. The condition is a property of the STATE, not of the render, so one
 * line per distinct state is all the information there is.
 *
 * Deliberately not reset: within a session the same cause recurring adds nothing, and a genuinely new
 * cause has a different key.
 */
const loggedHintSuppressions = new Set<string>();

/**
 * Drop the record of already-reported causes.
 *
 * Exists because a module-level cache with no way to clear it is test-hostile: any spec asserting "this
 * logs" would silently depend on no earlier spec having produced the same cause — a coupling that fails
 * confusingly and much later. Call it in a beforeEach. (It is also the honest seam if a caller ever wants
 * re-notification after the user fixes their stored zone.)
 */
export function clearCrossTimezoneHintWarningCache(): void {
	loggedHintSuppressions.clear();
}

function warnOncePerCause(key: string, message: string, context: Record<string, unknown>): void {
	if (loggedHintSuppressions.has(key)) {
		return;
	}
	loggedHintSuppressions.add(key);
	logWarn(message, context);
}

/*
 * schedulingTimezone.ts
 * LSP-17619 — helpers for the sticky per-user scheduling timezone.
 *
 * The Schedule Call picker used to default to the device zone on every booking, so a scheduler in
 * Asia booking for Chicago customers re-picked Chicago every single time. The zone she last
 * scheduled with is now remembered on her user record (Users.schedulingTimezone) and seeds the
 * picker; NULL means she has never picked one, which resolves to the device zone as before.
 */

/**
 * Resolve which timezone the scheduling picker should start on.
 *
 * `storedTimezone` is the user's remembered preference from `/me`. A null/absent value is the
 * normal state for anyone who has never changed the picker and simply means "use the device zone".
 * An unrecognized value is treated the same way, and not because anything hostile can reach the
 * column — both user-update routes filter it out by allowlist (LSP-20249). It is that the stored id
 * was validated against whichever client's tzdb wrote it, not this browser's: zone ids are renamed
 * and retired between releases, and moment already disagrees with the JVM over several. A zone this
 * moment build cannot resolve is a normal outcome, and a picker seeded with one breaks scheduling
 * outright — so resolve what we recognize and fall back cleanly on the rest.
 */
export function resolveSchedulingTimezone(storedTimezone?: string | null): string {
	// Resolve to the tzdb's own spelling rather than echoing the stored string. moment matches zone
	// names case-insensitively; mobile compares case-insensitively against TimeZone.availableZoneIds
	// for the same reason, so a row written before canonicalization (or by a direct API client)
	// resolves the same way on both platforms.
	const zone = storedTimezone ? moment.tz.zone(storedTimezone) : null;
	// Screened through the SCHEDULING picker's own rule rather than a second copy of it. A previous version
	// checked only Etc/*, so a stored EST / MST / bare GMT alias — all things the picker refuses to
	// offer, and which java.time cannot parse — would seed a picker with no matching entry to select,
	// and strand mobile on its device zone for the same account. Only reachable via the admin
	// PUT /users/:id passthrough, which is exactly the writer this read is not supposed to trust.
	if (zone && isSelectableSchedulingTimezone(zone.name)) {
		return zone.name;
	}
	// A stored value we could not resolve is silently dropped otherwise — the picker would just look
	// like it forgot the user's preference, with nothing to explain why. Mobile's equivalent path
	// logs the same case, so keep the two symmetric.
	if (storedTimezone) {
		logWarn("[LSP-17619] Ignoring an unrecognized stored scheduling timezone; falling back to the device zone", {
			storedTimezone: storedTimezone
		});
	}
	return selectableDeviceTimezone();
}

/**
 * The device's zone, unless the device's zone is one the scheduling picker refuses to offer.
 *
 * `moment.tz.guess()` returns `Intl.DateTimeFormat().resolvedOptions().timeZone` verbatim whenever that
 * is a known zone id, so a system configured with `Etc/GMT+5` (or a bare `GMT`) comes straight through.
 * Seeding that selects nothing in the dropdown and is rejected by the API on write — the same defect
 * contact-mobile's `defaultSelectableZone()` was added for, on the half of the fallback nobody had
 * screened.
 *
 * The substitute preserves the device's CURRENT OFFSET rather than defaulting to UTC.
 *
 * An earlier version returned the literal "UTC" and justified it as "reading the same clock as GMT, so
 * no displayed time moves". That is true only for the ZERO-offset aliases (Etc/UTC, Etc/GMT). The
 * Etc/GMT±N ids carry a real offset with POSIX sign inversion — Etc/GMT+5 is UTC-5 and Etc/GMT-8 is
 * UTC+8 — so substituting UTC handed a first-time user a picker defaulted 5 or 8 hours away from their
 * actual local time, silently. Matching the offset keeps every displayed time where the user expects it.
 *
 * Falls back to UTC only when nothing selectable shares the offset, which for a whole-hour Etc/GMT±N
 * offset does not happen in practice — but the guard stays, because an unmatched offset must still
 * produce a zone both pickers offer and the API accepts.
 */
export function selectableDeviceTimezone(): string {
	const guessed = moment.tz.guess();
	if (isSelectableSchedulingTimezone(guessed)) {
		return guessed;
	}
	const guessedOffset = moment.tz(guessed).utcOffset();
	// UTC first when it shares the offset, so the zero-offset aliases (Etc/UTC, Etc/GMT, bare GMT) keep
	// resolving to the id a reader expects instead of whichever equal-offset zone happens to sort first.
	if (guessedOffset === moment.tz("UTC").utcOffset()) {
		logWarn("[LSP-17619] Device timezone is not offered by the scheduling picker; falling back to UTC", {
			deviceTimezone: guessed
		});
		return "UTC";
	}
	// Matched across a WHOLE YEAR, not at two instants.
	//
	// The first version of this probed `now` and `now + 182 days` on the assumption that six months apart
	// lands in opposite DST states. It does not: US DST spans roughly 238 days, so for 112 days of the
	// year BOTH probes sit inside it, and a fixed Etc/GMT+8 still matched the DST-observing
	// America/Anchorage — right that day, an hour out every winter, and then persisted as the user's
	// sticky preference on their first booking. Sampling every ~30 days makes the comparison independent
	// of when it happens to run.
	//
	// Cheap probe first, then the full year only for the few candidates that survive it, so the common
	// case stays one offset lookup per zone rather than twelve.
	const yearProbes = Array.from({ length: 12 }, (_unused, index) => moment().add(index * 30, "days"));
	const guessedYearOffsets = yearProbes.map((probe) => probe.clone().tz(guessed).utcOffset());
	const offsetMatch = getSchedulingTimezones()
		.filter((zone) => moment.tz(zone.original).utcOffset() === guessedOffset)
		.find((zone) => yearProbes.every((probe, index) => probe.clone().tz(zone.original).utcOffset() === guessedYearOffsets[index]));
	if (offsetMatch) {
		logWarn("[LSP-17619] Device timezone is not offered by the scheduling picker; substituting an offset-equivalent zone", {
			deviceTimezone: guessed,
			substituted: offsetMatch.original,
			utcOffsetMinutes: guessedOffset
		});
		return offsetMatch.original;
	}
	logWarn("[LSP-17619] Device timezone is not offered by the scheduling picker and no selectable zone shares its offset", {
		deviceTimezone: guessed,
		utcOffsetMinutes: guessedOffset
	});
	return "UTC";
}

/**
 * Build the inline "9:00 AM Chicago · 10:00 PM your time" conversion line shown under the picker,
 * so a cross-timezone scheduler can sanity-check the slot without mental math.
 *
 * The time and date formats here are hardcoded (`h:mm A`, `MMM D`), whereas contact-mobile's
 * formatCrossTimeZoneHint honours the device's 12/24-hour setting and the org date format. That is a
 * known divergence: a 24-hour-clock user sees "2:00 PM Chicago" here and "14:00 Chicago" there for the
 * same booking. Closing it means reading those two preferences on this side too, which is a larger
 * change than the hint — so do not align one side to the other piecemeal.
 *
 * Returns null whenever the line would add nothing or would be wrong: no selection yet, an
 * unusable zone, or a viewer whose own clock reads the same as the selected zone at that instant
 * (comparing offsets rather than zone ids, so America/Chicago vs America/Winnipeg stays quiet).
 *
 * @param scheduledDate the picked calendar day — "YYYY-MM-DD", or the Date v-date-picker v-models
 * @param scheduledTime the picked wall-clock time, "h:mm A"
 * @param selectedTimezone the zone the picker is currently set to
 * @param viewerTimezone the viewer's own zone; defaults to the device zone
 */
/**
 * The DST shifts a fall-back overlap can be long, smallest first.
 *
 * A repeated wall clock recurs exactly one DST shift later, so finding the later occurrence means
 * knowing how big that shift is. This used to hardcode one hour, then [30, 60] — and the comment
 * here asserted that thirty minutes (Australia/Lord_Howe) was the tzdb's only exception to the hour.
 * That was simply false, which is worth stating plainly because the wrong claim is what made the
 * list look finished: Antarctica/Troll falls back by TWO HOURS, and it is a selectable zone.
 *
 * Verified against the tzdb rather than reasoned about: at 2026-10-25 Troll goes +02:00 → +00:00, so
 * 01:30 that morning occurs at 23:30Z and again at 01:30Z. The +30m and +60m probes land on 2:00 AM
 * and 2:30 AM, detect no repeat, and the still-bookable second occurrence is refused by the
 * past-slot guard — the identical failure Lord Howe had, one shift width along.
 *
 * No probe can match falsely, whatever the order: a match requires the instant N minutes later to
 * render the SAME wall clock, which happens only at that zone's own shift width. Two would have to
 * match for order to matter, and that needs two fall-backs within two hours of each other.
 */
const DST_FALL_BACK_SHIFT_MINUTES = [30, 60, 120];

/**
 * Does this wall clock occur twice — i.e. is it inside a DST fall-back overlap?
 *
 * Exported because RescheduleCallDialog needs the same question answered to decide whether to
 * disambiguate its summary with a zone abbreviation, and it had its own `add(1, "hour")` copy of the
 * rule. That is the defect shape this whole ticket keeps hitting: one rule encoded twice, fixed on
 * one side. The shift list is shared, so Lord Howe is now right in both places at once.
 */
export function isAmbiguousWallClock(parsed: moment.Moment): boolean {
	if (!parsed.isValid()) {
		return false;
	}
	const wallClock = parsed.format("YYYY-MM-DD h:mm A");
	return DST_FALL_BACK_SHIFT_MINUTES.some(
		(shiftMinutes) => parsed.clone().add(shiftMinutes, "minutes").format("YYYY-MM-DD h:mm A") === wallClock
	);
}

/**
 * LSP-17619 — does this wall clock NOT EXIST, i.e. is it inside a DST spring-forward gap?
 *
 * The mirror image of isAmbiguousWallClock. On a spring-forward day an hour is skipped entirely
 * (America/Chicago 2026-03-08 jumps 2:00 AM straight to 3:00 AM), so a wall clock inside it names no
 * real instant. `moment.tz` does not refuse one: it silently NORMALIZES forward and reports
 * isValid() === true, so 2:30 AM parses to a perfectly good 3:30 AM CDT. Nothing downstream can tell
 * the difference afterwards — the past-slot guard sees a valid future instant and waves it through.
 *
 * Detected by reformatting the parsed instant back to a wall clock and comparing: a mismatch means
 * moment had to move it, which only happens inside the gap.
 *
 * Shared, not copied. RescheduleCallDialog had the only implementation of this rule and
 * ActionSchedule had none, which is why the same picker could refuse a nonexistent time on one
 * screen and silently book an hour away from it on the other. That is the third time this ticket has
 * turned up one rule living in one of the two places that need it.
 */
export function isNonexistentWallClock(
	scheduledDate: string | Date | null | undefined,
	scheduledTime: string | null | undefined,
	timeZone: string | null | undefined
): boolean {
	if (!scheduledDate || !scheduledTime || !timeZone) {
		return false;
	}
	// A string that already NAMES a calendar day is used verbatim, never re-parsed (Bugbot, high).
	// The report's premise -- "a date-only string is parsed as UTC midnight, so any browser west of
	// UTC formats the previous day" -- does not hold: moment parses a date-only string as LOCAL, and
	// `TZ=Pacific/Honolulu node -e "moment('2026-03-08').format('YYYY-MM-DD')"` returns 2026-03-08,
	// as it does in every zone tried. The call sites are safe for a second reason too: Reschedule's
	// selectedDate setter formats a picked Date with local getters, and ActionSchedule's schedule()
	// has always round-tripped its own value through this identical moment(...).format(...) at the
	// `dateBase` line -- a shift there would have been booking the wrong DAY since long before this
	// helper existed.
	//
	// Hardened anyway, because the shape it describes is real one input along: an ISO timestamp
	// ("2026-03-08T00:00:00.000Z") IS an instant, and west of UTC it formats as the 7th. No caller
	// passes one today; taking the leading date means none ever can, and a wall-clock question is
	// about the day the caller named, not the instant a timestamp happens to fall in.
	const namedDay =
		typeof scheduledDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(scheduledDate)
			? scheduledDate.slice(0, 10)
			: moment(scheduledDate).format("YYYY-MM-DD");
	const requested = `${namedDay} ${scheduledTime}`;
	const parsed = moment.tz(requested, "YYYY-MM-DD h:mm A", timeZone);
	if (!parsed.isValid()) {
		return false;
	}
	return parsed.format("YYYY-MM-DD h:mm A") !== requested;
}

/**
 * LSP-17619 — resolve a parsed wall clock to the instant it will actually be BOOKED.
 *
 * A wall clock inside a DST fall-back overlap occurs twice and moment resolves the ambiguous string
 * to the FIRST (still-daylight) occurrence. Once that one has passed, the second is still bookable —
 * so the submit-time past-slot guard would otherwise refuse a legitimate booking, and, worse, the
 * invitation copy and this hint would name a different instant than `startAt`.
 *
 * ONE resolver for the guard, the submitted instant, the invitation tokens and the conversion line,
 * so those four cannot disagree about which occurrence is being booked. Mirrors mobile's
 * resolveScheduledInstant.
 *
 * "This hour repeated" is tested by the instant one hour later rendering the SAME wall clock — true
 * only inside the overlap. On an ordinary day 1:30 + 1h reads 2:30, and in a spring-forward GAP it
 * also reads differently, so an ordinary booking can never be shifted an hour.
 */
export function resolveBookableInstant(parsed: moment.Moment, nowMs: number = moment().valueOf()): moment.Moment {
	if (!parsed.isValid() || parsed.valueOf() > nowMs) {
		return parsed;
	}
	const wallClock = parsed.format("YYYY-MM-DD h:mm A");
	for (const shiftMinutes of DST_FALL_BACK_SHIFT_MINUTES) {
		const laterOccurrence = parsed.clone().add(shiftMinutes, "minutes");
		if (laterOccurrence.format("YYYY-MM-DD h:mm A") === wallClock && laterOccurrence.valueOf() > nowMs) {
			return laterOccurrence;
		}
	}
	return parsed;
}

export function formatCrossTimezoneHint(
	scheduledDate: string | Date,
	scheduledTime: string,
	selectedTimezone: string,
	viewerTimezone: string = moment.tz.guess()
): string | null {
	const dateString = toCalendarDayString(scheduledDate);
	if (!dateString || !scheduledTime || !selectedTimezone || !viewerTimezone) {
		return null;
	}
	if (!moment.tz.zone(selectedTimezone) || !moment.tz.zone(viewerTimezone)) {
		// Logged, unlike the three early-outs above it. Those return null for states that are normal and
		// expected — a not-yet-populated date or time. This one means a zone id reached here that moment
		// does not recognise, which is a real defect somewhere upstream (a stored preference from an
		// older tzdb, a hand-edited value) and is otherwise invisible: the hint simply does not render,
		// and a missing hint is also the correct output whenever both zones agree.
		warnOncePerCause(
			`zone:${selectedTimezone}|${viewerTimezone}`,
			"[LSP-17619] Cross-timezone hint suppressed: unrecognized zone id. The booking itself is unaffected.",
			{ selectedTimezone: selectedTimezone, viewerTimezone: viewerTimezone }
		);
		return null;
	}

	// The picked date and time are wall-clock values in the SELECTED zone — that pairing is what
	// fixes the absolute instant, which is then re-read in the viewer's zone.
	//
	// Parsed STRICTLY (the `true` argument). Moment's default leniency turns junk into a
	// confident-looking answer — "9:00 XM" and "09 AM" both silently become 9:00 AM — which here
	// would render a precise, wrong conversion rather than showing nothing. populateTimes() and
	// mounted() both emit "h:mm A" with no leading zero, so strict mode accepts every value the
	// component actually produces.
	// Routed through the shared resolver so the hint names the same occurrence schedule() submits;
	// otherwise, inside a fall-back overlap, "your time" is an hour off the booked slot.
	const selectedMoment = resolveBookableInstant(moment.tz(`${dateString} ${scheduledTime}`, "YYYY-MM-DD h:mm A", true, selectedTimezone));
	if (!selectedMoment.isValid()) {
		// Logged for the same reason as the unrecognized-zone branch above: strict parsing is what stops a
		// junk time rendering as a confident, wrong conversion, so reaching here means the component
		// produced a time string its own consumer cannot read. The hint just disappears, which is also
		// what "the two zones agree" looks like — indistinguishable without this line.
		warnOncePerCause(
			// Keyed on the CAUSE and the zone, deliberately NOT on dateString/scheduledTime. Those change on
			// every keystroke, so including them meant a new key each time: the log fired repeatedly for one
			// persistent problem and the dedup Set grew unbounded for the tab's lifetime — the exact noise
			// the dedup was added to stop.
			`parse:${selectedTimezone}`,
			"[LSP-17619] Cross-timezone hint suppressed: could not strictly parse the selected slot",
			{ dateString: dateString, scheduledTime: scheduledTime, selectedTimezone: selectedTimezone }
		);
		return null;
	}

	const viewerMoment = selectedMoment.clone().tz(viewerTimezone);
	if (selectedMoment.utcOffset() === viewerMoment.utcOffset()) {
		return null;
	}

	// Only the day differing matters for comprehension; a same-day conversion reads fine as a bare
	// time, while a cross-midnight one is misleading without the date.
	const daysDiffer = selectedMoment.format("YYYY-MM-DD") !== viewerMoment.format("YYYY-MM-DD");
	const viewerPart = daysDiffer ? viewerMoment.format("h:mm A MMM D") : viewerMoment.format("h:mm A");

	return `${selectedMoment.format("h:mm A")} ${formatZoneLabel(selectedTimezone)} · ${viewerPart} your time`;
}

// scheduledDate is a "YYYY-MM-DD" string until the user opens the calendar, at which point
// v-date-picker v-models a Date carrying browser-local midnight. Read that Date through its local
// components rather than as an absolute instant: moment(date).tz(zone) would reinterpret the
// instant in the target zone and can slide the calendar day backward. Same normalization as
// ActionSchedule's updateMinimumDate().
function toCalendarDayString(scheduledDate: string | Date): string {
	if (scheduledDate instanceof Date) {
		if (Number.isNaN(scheduledDate.getTime())) {
			return "";
		}
		const month = String(scheduledDate.getMonth() + 1).padStart(2, "0");
		const day = String(scheduledDate.getDate()).padStart(2, "0");
		return `${scheduledDate.getFullYear()}-${month}-${day}`;
	}
	return scheduledDate;
}

// "America/New_York" reads as "New York" — the city is the part a user recognizes, and the IANA
// region prefix and underscores are noise in a one-line hint.
function formatZoneLabel(timezone: string): string {
	const segments = timezone.split("/");
	return segments[segments.length - 1].replaceAll("_", " ");
}

/**
 * LSP-21581 — the instant a zone LABEL (abbreviation, GMT offset) should describe.
 *
 * A zone's abbreviation and offset are properties of an instant, not of the zone: America/New_York
 * is EDT (-04:00) in October and EST (-05:00) in November. Both scheduling screens built their
 * "(GMT-04:00) America/New York EDT" chip and "America/New York · EDT" header from
 * `moment.tz(zone).format("z")`, which is the zone AT THE MOMENT OF RENDERING — so an agent booking a
 * 2 November call on 6 October saw EDT on the chip while the message on the same screen, the
 * session card, the SMS and the email all said EST. The offset was wrong by an hour for anyone who
 * used it to work out the time.
 *
 * Resolution order mirrors what the user has told us so far:
 *   1. date AND time picked — that wall clock in the zone, routed through resolveBookableInstant so a
 *      fall-back overlap names the same occurrence schedule() will submit;
 *   2. date only — midday on that day in the zone. The abbreviation is a property of the DAY the
 *      user picked, and midday is clear of every transition hour, so the chip is right the moment the
 *      date is chosen rather than only once a time follows it;
 *   3. nothing yet — `fallback` (now), the only honest answer before a selection exists.
 *
 * Strictly parsed for the same reason as formatCrossTimezoneHint: a junk time must not become a
 * confident instant. It falls through to the date-only anchor instead, which is still the right day.
 *
 * @returns a moment IN `timeZone`, ready for `.format("z")` / `.format("Z")`.
 */
export function resolveZoneLabelInstant(
	scheduledDate: string | Date | null | undefined,
	scheduledTime: string | null | undefined,
	timeZone: string,
	fallback: moment.Moment = moment()
): moment.Moment {
	// A string that names a calendar day is taken verbatim; an ISO timestamp is an INSTANT and west of
	// UTC formats as the previous day, so only its leading date is kept (same hardening as
	// isNonexistentWallClock).
	const calendarDay = scheduledDate ? toCalendarDayString(scheduledDate) : "";
	const namedDay = /^\d{4}-\d{2}-\d{2}/.test(calendarDay) ? calendarDay.slice(0, 10) : "";
	if (!namedDay) {
		return fallback.clone().tz(timeZone);
	}
	if (scheduledTime) {
		const parsed = moment.tz(`${namedDay} ${scheduledTime}`, "YYYY-MM-DD h:mm A", true, timeZone);
		if (parsed.isValid()) {
			// Overlap resolution asks "has the first occurrence passed", which is a question about the real
			// clock -- never about `fallback`, which a caller may set to the booked start.
			return resolveBookableInstant(parsed);
		}
	}
	return moment.tz(`${namedDay} 12:00`, "YYYY-MM-DD HH:mm", true, timeZone);
}
